import { useToast } from '@chakra-ui/react';
import React, { createContext, useContext, useState, useEffect } from 'react';

import {
  error as commonError,
  finleyAssert,
  utils as commonUtils,
} from '@finley-technologies/common';

import { useGetBbplMonitorStatusQuery } from '../requests/bbplMonitor.gql.generated';
import { BBPLStatus } from '../types/bbplMonitorTypes';
import { useDocumentVisibility } from './useDocumentVisibility';

interface StatusContextType {
  status: BBPLStatus;
}

const isSameStatus = (
  status1: BBPLStatus | null,
  status2: BBPLStatus | null,
): boolean => {
  if (!status1 && !status2) {
    // both are null
    return true;
  }

  if (!status1 || !status2) {
    // only one is null
    return false;
  }

  const same =
    status1.creditFacilityId === status2.creditFacilityId &&
    status1.borrowerOrganizationId === status2.borrowerOrganizationId &&
    status1.mainArtifactDocumentId === status2.mainArtifactDocumentId &&
    status1.mostRecentRDFV?.fileVersion ===
      status2.mostRecentRDFV?.fileVersion &&
    status1.mostRecentRDFV?.reportingDate ===
      status2.mostRecentRDFV?.reportingDate;

  return same;
};

const StatusContext = createContext<StatusContextType | undefined>(undefined);

export const StatusProvider = ({
  children,
  placeHolder,
  creditFacilityId,
}: {
  children: React.ReactNode;
  placeHolder: React.ReactNode;
  creditFacilityId: string;
}) => {
  const toast = useToast();
  const [status, setStatus] = useState<BBPLStatus | null>(null);
  const isDocumentVisible = useDocumentVisibility();

  const {
    data: bbplMonitorStatusData,
    startPolling: startPollingBbplMonitorStatus,
    stopPolling: stopPollingBbplMonitorStatus,
  } = useGetBbplMonitorStatusQuery({
    fetchPolicy: 'network-only',
    variables: {
      creditFacilityId,
    },
    onError: (error) => {
      // only toast on stage + dev
      if (!commonUtils.EnvUtils.isProduction()) {
        const errorMessage = commonError.getErrorMessage(error);
        toast({
          title: 'Error fetching BBPL Monitor status',
          status: 'error',
          description: errorMessage,
          isClosable: true,
        });
      }
    },
  });

  useEffect(() => {
    let pollingStarted = false;
    if (isDocumentVisible) {
      startPollingBbplMonitorStatus(5000);
      pollingStarted = true;
    }
    return () => {
      if (pollingStarted) {
        stopPollingBbplMonitorStatus();
      }
    };
  }, [
    isDocumentVisible,
    startPollingBbplMonitorStatus,
    stopPollingBbplMonitorStatus,
  ]);

  useEffect(() => {
    if (bbplMonitorStatusData) {
      if (bbplMonitorStatusData.bbplMonitorStatus.latestArtifactDocumentId) {
        const latestInstance =
          bbplMonitorStatusData.bbplMonitorStatus.latestArtifactInstance;
        const mostRecentRDFV = latestInstance
          ? {
              reportingDate: latestInstance.reportingDate,
              fileVersion: latestInstance.fileVersion,
            }
          : null;

        const newStatus = {
          borrowerOrganizationId:
            bbplMonitorStatusData.bbplMonitorStatus.borrowerId,
          creditFacilityId,
          mainArtifactDocumentId:
            bbplMonitorStatusData.bbplMonitorStatus.latestArtifactDocumentId,
          mostRecentRDFV,
        };
        if (!isSameStatus(status, newStatus)) {
          setStatus(newStatus);
        }
      }
    }
  }, [bbplMonitorStatusData, status, creditFacilityId]);

  if (!status) {
    return <>{placeHolder}</>;
  }

  return (
    <StatusContext.Provider value={{ status }}>
      {children}
    </StatusContext.Provider>
  );
};

export const useStatus = () => {
  const context = useContext(StatusContext);
  finleyAssert(context, 'useStatus must be used within a StatusProvider');
  return context;
};
